import React, { useEffect, useMemo, useState } from "react";
import { getRouteMenus } from "./VerticalMenuUtils";
import { StyledVerticalNav } from "./index.styled";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { IRouteConfig } from "@lib/types/Routes";
import { useTypedSelector } from "@lib/redux";
import {
  analyzePathnameForNavigation,
  findSelectedKeys,
  getSelectedKeyFromPathname,
  findRouteById,
} from "@lib/helpers/UrlHelper";

type Props = {
  routesConfig: IRouteConfig[];
  isDrawer?: boolean;
};

const AppVerticalNav: React.FC<Props> = ({
  routesConfig,
  isDrawer = false,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  // const { sidebar } = useTypedSelector((state) => state.settings);
  const { collapsed, hidden, width } = useTypedSelector(
    (state) => state.screen.sidebar,
  );
  const items = getRouteMenus(routesConfig, messages);

  const { pathname } = useLocation();
  const params = useParams();
  const keys = useMemo(() => {
    return findSelectedKeys({
      routes: routesConfig,
      path: pathname,
      params,
    });
  }, [pathname, params]);

  // Use optimized helper functions for URL analysis
  const pathAnalysis = useMemo(
    () => analyzePathnameForNavigation(pathname),
    [pathname],
  );
  const {
    selectedKeys,
    // selectedPaths,
    selectedKey: initialSelectedKey,
  } = pathAnalysis;

  const [openKeys, setOpenKeys] = useState([selectedKeys[0]]);
  const [selectedKey, setSelectedKey] = useState(initialSelectedKey);

  useEffect(() => {
    if (pathname) {
      setSelectedKey(getSelectedKeyFromPathname(pathname));
    }
  }, [pathname]);

  const handleSelect = ({ key }: any) => {
    setSelectedKey(key);

    // If sidebar is collapsed, navigate to the route URL
    // In collapsed mode, Link component doesn't work properly, so we manually navigate
    if (collapsed && !isDrawer) {
      const route = findRouteById(key, routesConfig);
      if (route?.url) {
        navigate(route.url);
      }
    }
  };

  const onOpenChange = (keys: string[]) => {
    const latestOpenKey = keys.find(
      (key: string) => openKeys.indexOf(key) === -1,
    );
    setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
  };

  if (hidden && !isDrawer) {
    return null;
  }

  return (
    <StyledVerticalNav
      mode="inline"
      inlineCollapsed={collapsed && !isDrawer}
      forceSubMenuRender={true}
      onOpenChange={onOpenChange}
      onClick={handleSelect}
      defaultOpenKeys={width == 50 ? [] : keys.found}
      selectedKeys={[selectedKey!, ...keys.found!]}
      items={items as any[]}
    />
  );
};

export default AppVerticalNav;
